import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Trash2, Users, Loader2, Pencil, Network, FileBarChart, Plus } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", roles: [], extra_menu_ids: [], structures: [], home_menu_id: "" };

export default function UserManagement() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const { data: users, isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: roles } = useQuery({ queryKey: ["admin-roles"], queryFn: async () => (await api.get("/admin/roles")).data });
  const { data: menu } = useQuery({ queryKey: ["admin-menu"], queryFn: async () => (await api.get("/admin/menu")).data });
  const { data: structures } = useQuery({ queryKey: ["admin-structures"], queryFn: async () => (await api.get("/structures")).data });

  const assignable = useMemo(() => (menu || []).filter((m) =>
    m.type === "reporte" || m.type === "capability" ||
    (m.type === "menu" && m.route && m.id !== "estructura_organizacional" && !m.admin_only)
  ), [menu]);
  const menuById = useMemo(() => Object.fromEntries((menu || []).map((m) => [m.id, m])), [menu]);
  const menuLabel = (r) => { const p = menuById[r.parent_id]; return p ? `${p.name} · ${r.name}` : r.name; };
  const shared = { roles, menu, structures, assignable, menuLabel };

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/users", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Usuario creado"); setOpen(false); setForm(EMPTY); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.patch(`/users/${id}`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Usuario actualizado"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const editMut = useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.patch(`/users/${id}`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Usuario actualizado"); setEditing(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Usuario eliminado"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submit = (e) => { e.preventDefault(); createMut.mutate(form); };

  const openEdit = async (u) => {
    setEditing({ id: u.id, name: u.name, email: u.email, roles: u.roles || [], extra_menu_ids: [], structures: [], home_menu_id: "" });
    try {
      const p = (await api.get(`/users/${u.id}/permissions`)).data;
      setEditing((cur) => cur && cur.id === u.id ? {
        ...cur, roles: p.roles || [], extra_menu_ids: p.extra_menu_ids || [],
        structures: (p.structures || []).map((s) => ({ structure_key: s.structure_key, node_id: s.node_id || null, include_descendants: s.include_descendants !== false })),
        home_menu_id: p.home_menu_id || "", is_admin: p.is_admin,
      } : cur);
    } catch (e) {
      console.error("No se pudieron cargar los permisos del usuario:", e);
      toast.error("No se pudieron cargar los permisos del usuario");
    }
  };
  const submitEdit = (e) => {
    e.preventDefault();
    editMut.mutate({ id: editing.id, name: editing.name, roles: editing.roles, extra_menu_ids: editing.extra_menu_ids, structures: editing.structures, home_menu_id: editing.home_menu_id || "" });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Administración</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <Users className="w-8 h-8 text-red-500" /> Usuarios
          </h1>
          <p className="text-slate-400 text-sm mt-1">El rol es una plantilla de permisos. La estructura y los permisos adicionales se configuran por usuario.</p>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(EMPTY); }}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="create-user-modal-button">
              <UserPlus className="w-4 h-4 mr-2" /> Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-display">Crear Usuario</DialogTitle>
              <DialogDescription className="text-slate-400">Configura rol, permisos adicionales, estructuras y pantalla inicial desde el inicio.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>
                <div><Label className="text-slate-300">Correo</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>
              </div>
              <div><Label className="text-slate-300">Contraseña</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>

              <PermFields value={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} {...shared} isAdmin={false} />

              <DialogFooter>
                <Button type="submit" disabled={createMut.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-user-button">
                  {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear usuario"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden shadow-lg">
        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-[#223048] hover:bg-transparent">
                <TableHead className="text-slate-400">Usuario</TableHead>
                <TableHead className="text-slate-400">Rol</TableHead>
                <TableHead className="text-slate-400">Estructuras</TableHead>
                <TableHead className="text-slate-400">Estado</TableHead>
                <TableHead className="text-slate-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users?.map((u) => (
                <TableRow key={u.id} className="border-[#223048]" data-testid={`user-row-${u.email}`}>
                  <TableCell><p className="font-medium text-white">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.role_labels?.length ? u.role_labels : ["Sin rol"]).map((r, i) => (
                        <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-600/15 text-red-400 border border-red-600/30">{r}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-400" data-testid={`user-structs-${u.email}`}>
                      {u.is_admin ? "Todas" : ((u.structures?.length || 0) + " asignada" + ((u.structures?.length || 0) === 1 ? "" : "s"))}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Switch checked={u.active !== false} onCheckedChange={(v) => updateMut.mutate({ id: u.id, active: v })} data-testid={`toggle-active-${u.email}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} className="text-slate-400 hover:text-red-400 hover:bg-red-600/10" data-testid={`edit-user-${u.email}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (window.confirm(`¿Eliminar a ${u.name}?`)) deleteMut.mutate(u.id); }} className="text-slate-400 hover:text-red-400 hover:bg-red-600/10" data-testid={`delete-user-${u.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-display">Configurar Usuario</DialogTitle>
            <DialogDescription className="text-slate-400">{editing?.email} — rol (plantilla) + permisos y estructuras propias.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={submitEdit} className="space-y-5">
              <div><Label className="text-slate-300">Nombre</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} required data-testid="edit-user-name-input" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>

              <PermFields value={editing} onChange={(patch) => setEditing((u) => ({ ...u, ...patch }))} {...shared} isAdmin={editing.is_admin} editPrefix />

              <DialogFooter>
                <Button type="submit" disabled={editMut.isPending} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-edit-user-button">
                  {editMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermFields({ value, onChange, roles, menu, structures, assignable, menuLabel, isAdmin, editPrefix }) {
  const [inherited, setInherited] = useState([]);
  const rolesKey = JSON.stringify(value.roles || []);
  useEffect(() => {
    let cancelled = false;
    const rks = value.roles || [];
    if (!rks.length) { setInherited([]); return; }
    Promise.all(rks.map((rk) => api.get(`/admin/roles/${rk}/permissions`).then((r) => r.data.menu_ids || []).catch(() => [])))
      .then((res) => { if (!cancelled) setInherited([...new Set(res.flat())]); });
    return () => { cancelled = true; };
  }, [rolesKey]);

  const toggleRole = (key) => onChange({ roles: value.roles.includes(key) ? value.roles.filter((r) => r !== key) : [...value.roles, key] });
  const toggleExtra = (id) => onChange({ extra_menu_ids: value.extra_menu_ids.includes(id) ? value.extra_menu_ids.filter((x) => x !== id) : [...value.extra_menu_ids, id] });
  const rp = editPrefix ? "edit-" : "";

  const accessSet = new Set([...(inherited || []), ...(value.extra_menu_ids || [])]);
  const homeOptions = (menu || []).filter((m) => m.route && !m.admin_only && (isAdmin || accessSet.has(m.id)));

  return (
    <>
      <div>
        <Label className="text-slate-300">Rol (opcional · plantilla de permisos)</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5 max-h-36 overflow-auto pr-1">
          {roles?.map((r) => (
            <label key={r.key} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-950/50 border border-slate-700 cursor-pointer hover:border-red-600/40" data-testid={`${rp}role-checkbox-${r.key}`}>
              <Checkbox checked={value.roles.includes(r.key)} onCheckedChange={() => toggleRole(r.key)} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
              <span className="text-xs text-slate-200">{r.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Menú Principal (pantalla inicial)</Label>
        <p className="text-[11px] text-slate-500 mt-1">Reporte que se abrirá al ingresar. Solo aparecen los que el usuario puede ver. Si queda sin configurar, abre el Dashboard.</p>
        <Select value={value.home_menu_id || "__default__"} onValueChange={(v) => onChange({ home_menu_id: v === "__default__" ? "" : v })}>
          <SelectTrigger className="mt-1.5 h-10 bg-slate-950/60 border-slate-700 text-white" data-testid="home-menu-select"><SelectValue placeholder="Predeterminado (Dashboard)" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
            <SelectItem value="__default__">Predeterminado (Dashboard)</SelectItem>
            {homeOptions.map((m) => <SelectItem key={m.id} value={m.id}>{menuLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isAdmin ? (
        <p className="text-sm text-slate-400 bg-slate-950/40 border border-[#223048] rounded-lg p-3">Este usuario es Administrador Global: tiene acceso a todos los menús, reportes y estructuras.</p>
      ) : (
        <>
          <div>
            <Label className="text-slate-300 flex items-center gap-2"><FileBarChart className="w-4 h-4 text-red-500" /> Menús y reportes</Label>
            <p className="text-[11px] text-slate-500 mt-1">Los marcados con <span className="text-amber-400">(rol)</span> se heredan del rol. Marca otros para asignarlos directamente al usuario.</p>
            <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-auto pr-1">
              {assignable.map((r) => {
                const isInherited = inherited.includes(r.id);
                const checked = isInherited || value.extra_menu_ids.includes(r.id);
                return (
                  <label key={r.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${isInherited ? "bg-amber-500/5 border-amber-500/20 cursor-default" : "bg-slate-950/50 border-slate-700 cursor-pointer hover:border-red-600/40"}`} data-testid={`${rp}extra-menu-${r.id}`}>
                    <Checkbox checked={checked} disabled={isInherited} onCheckedChange={() => !isInherited && toggleExtra(r.id)} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                    <span className="text-xs text-slate-200">{menuLabel(r)}{isInherited && <span className="text-amber-400 ml-1">(rol)</span>}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-slate-300 flex items-center gap-2"><Network className="w-4 h-4 text-red-500" /> Estructuras organizacionales</Label>
            <p className="text-[11px] text-slate-500 mt-1">Definen sobre qué información puede operar el usuario. Sin nodo = estructura completa; un nodo incluye sus dependencias.</p>
            <div className="mt-1.5 space-y-2">
              {value.structures.map((sp, i) => (
                <StructRow key={i} sp={sp} structures={structures}
                  onChange={(val) => onChange({ structures: value.structures.map((x, j) => j === i ? val : x) })}
                  onRemove={() => onChange({ structures: value.structures.filter((_, j) => j !== i) })} index={i} />
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => onChange({ structures: [...value.structures, { structure_key: structures?.[0]?.key || "", node_id: null, include_descendants: true }] })} className="border-dashed border-[#223048] text-slate-300 w-full" data-testid="add-user-struct">
                <Plus className="w-4 h-4 mr-1.5" /> Agregar estructura
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function StructRow({ sp, structures, onChange, onRemove, index }) {
  const { data: nodes } = useQuery({
    queryKey: ["ctx-tree", sp.structure_key],
    queryFn: async () => (await api.get(`/context/tree?structure=${sp.structure_key}`)).data,
    enabled: !!sp.structure_key,
  });
  const byId = useMemo(() => Object.fromEntries((nodes || []).map((n) => [n.id, n])), [nodes]);
  const childrenOf = useMemo(() => {
    const map = {};
    (nodes || []).forEach((n) => { const p = n.parent_id || "__root__"; (map[p] = map[p] || []).push(n); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name, "es")));
    return map;
  }, [nodes]);

  const selected = sp.node_id ? byId[sp.node_id] : null;
  const path = selected ? [...(selected.ancestors || []).map((a) => a.id), selected.id] : [];
  const levels = [];
  let parentKey = "__root__";
  for (let i = 0; ; i++) {
    const options = childrenOf[parentKey] || [];
    if (!options.length) break;
    const val = path[i] || null;
    levels.push({ parentKey, options, value: val });
    if (!val) break;
    parentKey = val;
  }

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-stretch sm:items-center bg-slate-900/40 border border-[#223048] rounded-lg p-2" data-testid={`struct-row-${index}`}>
      <Select value={sp.structure_key} onValueChange={(v) => onChange({ ...sp, structure_key: v, node_id: null })}>
        <SelectTrigger className="h-9 bg-[#0F172A] border-[#223048] text-white sm:w-56" data-testid={`struct-select-${index}`}><SelectValue placeholder="Estructura" /></SelectTrigger>
        <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200">
          {structures?.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {levels.map((lv, i) => {
        const levelLabel = lv.options[0]?.label || "Nivel";
        return (
          <Select key={i} value={lv.value || "__all__"}
            onValueChange={(v) => onChange({ ...sp, node_id: v === "__all__" ? (i === 0 ? null : lv.parentKey) : v })}>
            <SelectTrigger className="h-9 bg-[#0F172A] border-[#223048] text-white min-w-[10rem] flex-1" data-testid={`node-select-${index}-${i}`}>
              <SelectValue placeholder={levelLabel} />
            </SelectTrigger>
            <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
              <SelectItem value="__all__">{i === 0 ? "Estructura completa" : `↳ Toda la rama (${byId[lv.parentKey]?.name || ""})`}</SelectItem>
              {lv.options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}: {o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      })}
      <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-slate-400 hover:text-red-400 hover:bg-red-600/10 shrink-0" data-testid={`remove-struct-${index}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}
